// The bottom of every page. Terms, privacy, the FAQ and the forum live here,
// which is where people look for them, rather than in the top navigation beside
// the thing they came to do.
//
// The footer's background is a photographed plant bank, one per theme.

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import '../../styles/footer-refinements.css';

const NARROW = '(max-width: 429px)';

function useNarrow(): boolean {
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia(NARROW).matches);
  useEffect(() => {
    const query = window.matchMedia(NARROW);
    const update = () => setNarrow(query.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  return narrow;
}

// On a narrow phone each group folds into its heading, so the footer is four
// lines rather than a second navigation screen.
function FooterGroup({ title, narrow, children }: { title: string; narrow: boolean; children: ReactNode }) {
  if (narrow) {
    return (
      <details className="footgroup">
        <summary>{title}</summary>
        {children}
      </details>
    );
  }
  return (
    <div>
      <h4>{title}</h4>
      {children}
    </div>
  );
}

export function SiteFooter() {
  const narrow = useNarrow();
  return (
    <footer className="sitefooter">
      <div className="footcols">
        <FooterGroup title="Explore" narrow={narrow}>
          <Link to="/home">Home</Link>
          <Link to="/plan">Plan</Link>
          <Link to="/lineup">Lineup</Link>
          <Link to="/news">News &amp; updates</Link>
          <Link to="/weather">Weather &amp; radar</Link>
        </FooterGroup>
        <FooterGroup title="Community" narrow={narrow}>
          <Link to="/forum">Forum</Link>
          <Link to="/search">People and groups</Link>
          <Link to="/photos">Photos</Link>
          <Link to="/art">Artists’ corner</Link>
        </FooterGroup>
        <FooterGroup title="Help" narrow={narrow}>
          <Link to="/faq">App help</Link>
          <Link to="/links">Festival resources</Link>
          <Link to="/feedback">Requests and feedback</Link>
        </FooterGroup>
        <FooterGroup title="Legal" narrow={narrow}>
          <Link to="/terms">Terms</Link>
          <Link to="/privacy">Privacy</Link>
        </FooterGroup>
      </div>
      <p className="footnote">
        Set times are published by Lost Lands and may change. This is an unofficial community
        planner.
      </p>
    </footer>
  );
}
